// Inline edit mode for a message body.
//
// Replaces the rendered text with a `<textarea>` and a Save / Cancel
// action row. Auto-grows to the brief's 40–200px range; Enter saves,
// Shift+Enter inserts a newline, Escape cancels. Errors on save land in
// the action row copy with `role="status"` + `aria-live` so screen-reader
// users hear the rejection instead of seeing an eternally-spinning button.

import { useId, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useAutoResizeTextarea } from "@nurion/ui/use-auto-resize-textarea";

import { GhostButton, PrimaryButton } from "~/components/MessageButtons";

const INLINE_EDIT_MIN_HEIGHT_CSS_VAR = "--chat-inline-edit-min-height";
const INLINE_EDIT_MAX_HEIGHT_CSS_VAR = "--chat-inline-edit-max-height";

interface InlineEditProps {
  initialBody: string;
  onSave: (next: string) => Promise<void>;
  onCancel: () => void;
  onRecallOlder?: () => void;
  onRecallNewer?: () => void;
}

export function InlineEdit({
  initialBody,
  onSave,
  onCancel,
  onRecallOlder,
  onRecallNewer,
}: InlineEditProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [value, setValue] = useState(initialBody);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Unique id per mount so two concurrent `InlineEdit`s (timeline +
  // thread panel) don't collide on `aria-describedby`.
  const statusId = `${useId()}-status`;

  // Focus on mount with the cursor at the end, matching Slack / Element
  // muscle memory. `useLayoutEffect` ensures the focus lands before any
  // paint so keyboard users don't see a flash of "not focused".
  // We also remember the previously focused element and restore it on
  // unmount so Cancel / Save drop the user back where they came from
  // (the hover Edit button).
  useLayoutEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const ta = textareaRef.current;
    if (!ta) return;
    ta.focus();
    ta.setSelectionRange(ta.value.length, ta.value.length);
    return () => {
      // Only restore focus if the previously focused element is still
      // mounted — on a message row the hover Edit button disappears
      // when the row leaves hover, so chasing it would throw focus
      // into the void. The browser's default (fall back to body) is
      // the right behaviour when the anchor is gone.
      const prev = previouslyFocusedRef.current;
      if (prev && prev.isConnected) prev.focus?.();
    };
  }, []);

  useAutoResizeTextarea(textareaRef, value, {
    min: { name: INLINE_EDIT_MIN_HEIGHT_CSS_VAR, fallback: 40 },
    max: { name: INLINE_EDIT_MAX_HEIGHT_CSS_VAR, fallback: 200 },
  });

  const canSave = value.trim().length > 0 && value !== initialBody && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(value.trim());
      // Success leaves `saving` true because the parent unmounts us in
      // the same tick. If the contract ever changes (inline save
      // without close), the finally branch below takes over — keeping
      // both is the cheapest correct option.
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't save the edit.");
    } finally {
      setSaving(false);
    }
  }

  const autoGrow: CSSProperties = {
    minHeight: "var(--chat-inline-edit-min-height)",
    maxHeight: "var(--chat-inline-edit-max-height)",
  };

  return (
    <div>
      <div className="chat-composer-wrapper">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onCancel();
              return;
            }
            if (e.key === "ArrowUp" && onRecallOlder && !e.nativeEvent.isComposing) {
              const atStart =
                e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 0;
              if (atStart) {
                e.preventDefault();
                onRecallOlder();
              }
              return;
            }
            if (e.key === "ArrowDown" && onRecallNewer && !e.nativeEvent.isComposing) {
              const end = e.currentTarget.value.length;
              const atEnd =
                e.currentTarget.selectionStart === end && e.currentTarget.selectionEnd === end;
              if (atEnd) {
                e.preventDefault();
                onRecallNewer();
              }
              return;
            }
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSave();
            }
          }}
          aria-label="Edit message"
          aria-invalid={error !== null}
          aria-describedby={error ? statusId : undefined}
          style={{
            display: "block",
            width: "100%",
            padding: "var(--chat-space-1) 0",
            border: "none",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-lg-size)",
            lineHeight: "var(--chat-type-body-lg-line)",
            resize: "none",
            outline: "none",
            ...autoGrow,
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "var(--chat-inline-edit-action-row-height)",
          marginTop: "var(--chat-space-2)",
          gap: "var(--chat-space-2)",
        }}
      >
        <StatusLine id={statusId} error={error} />
        <GhostButton onClick={onCancel} disabled={saving}>
          Cancel
        </GhostButton>
        <PrimaryButton onClick={() => void handleSave()} disabled={!canSave}>
          Save
        </PrimaryButton>
      </div>
    </div>
  );
}

/**
 * Live region for the inline-edit action row. `role="status"` +
 * `aria-live="polite"` means AT users hear save errors without the
 * announcement interrupting whatever they were reading. Idle copy
 * ("Esc to cancel") stays silent — polite regions only fire on
 * meaningful change.
 */
function StatusLine({ id, error }: { id: string; error: string | null }) {
  return (
    <span
      id={id}
      role="status"
      aria-live="polite"
      style={{
        flex: 1,
        color: error ? "var(--chat-state-danger)" : "var(--chat-fg-muted)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
      }}
    >
      {error ?? "Esc to cancel"}
    </span>
  );
}
